import React, { useState, useEffect, useMemo } from "react";
import { Badge, Card, Collapse, Row, Col, Table, Button, Spinner, Form, InputGroup, Pagination, Modal } from "react-bootstrap";
import { FaEye, FaFilter, FaPlus, FaDollarSign, FaTimes } from "react-icons/fa";
import { BsBookmarkPlusFill } from "react-icons/bs";
import { useNavigate, useLocation } from "react-router-dom";
import { useTickets } from "../../../hooks/TICKET/useGetTickets";
import { useCloseTicket } from "../../../hooks/TICKET/useCloseTicket";
import { useFacturationModes } from "../../../hooks/TICKET/useFacturationModes";
import { useContratsByClientId } from "../../../hooks/useGetContratClient";
import { useTicketTypes } from "../../../hooks/TICKET/useTicketTypes";
import { useTicketPriorities } from "../../../hooks/TICKET/useTicketPriorities";
import { useUsers } from "../../../hooks/useGetUsers";
import { useAuth } from "../../../context/AuthContext";
import Select from "react-select";
import { customSelectStyles } from "../../../utils/reactSelectStyles";
import { getStatusVariant, statusNeedsDarkText, TICKET_STATUS_LABELS } from "../../../utils/ticketStatus";
import NewTicket from "../../Modals/Create/NewTicket";
import NewIntervention from "../../Modals/Create/NewIntervention";
import { toast } from "react-toastify";

const ALL_STATUSES = ["open", "in_progress", "closed", "approved"];

// Variante des filter pills (closed sans distinction maintenance car on filtre par statut seulement)
const FILTER_BADGE = {
  open:        "danger",
  in_progress: "warning",
  closed:      "primary",
  approved:    "secondary",
};

const OP_OPTIONS = [
  { value: "=",  label: "=" },
  { value: ">=", label: "≥" },
  { value: "<=", label: "≤" },
  { value: ">",  label: ">" },
  { value: "<",  label: "<" },
];

const MOBILE_BREAKPOINT = 1000;

const CONTRAT_MAINTENANCE = { id: 1, label: "Contrat de maintenance" };

const TicketMiniRow = ({ ticket, onNavigate, clientId, asCard = false }) => {
  const { hasPermission } = useAuth();
  const [showNewIntervention, setShowNewIntervention] = useState(false);
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [selectedFacturationMode, setSelectedFacturationMode] = useState(null);

  const { data: facturationModesData, isLoading: facturationLoading } = useFacturationModes();
  const { data: contratData } = useContratsByClientId(clientId);
  const closeMutation = useCloseTicket();

  const sortedInterventions = useMemo(() => {
    // Les interventions sont déjà incluses dans la réponse API
    const interventions = ticket.ticket_interventions || [];
    return [...interventions].sort((a, b) => {
      const aTime = new Date(a?.start_date || a?.created_at || 0).getTime();
      const bTime = new Date(b?.start_date || b?.created_at || 0).getTime();
      return bTime - aTime;
    });
  }, [ticket]);
  const groupNames = ticket.groups?.map(g => g.name) || [];
  const userNames  = ticket.users?.map(u => `${u.name} ${u.surname}`) || [];
  const facturationModes = useMemo(() => facturationModesData?.data || [], [facturationModesData]);
  const contracts   = Array.isArray(contratData?.data) ? contratData.data : [];
  const hasContract = contracts.some(c => !c.expire_date || new Date(c.expire_date) > new Date());

  useEffect(() => {
    if (showConfirmClose && facturationModes.length > 0 && !selectedFacturationMode && hasContract !== undefined) {
      if (hasContract) {
        setSelectedFacturationMode(1);
      } else {
        const firstMode = facturationModes.find(m => (m.is_active || m.is_default) && m.id !== 1);
        setSelectedFacturationMode(firstMode?.id);
      }
    }
  }, [showConfirmClose, facturationModes, selectedFacturationMode, hasContract]);

  const formatDate = (value) => {
    if (!value) return "N/A";
    try {
      const d = new Date(value);
      if (isNaN(d.getTime())) return "N/A";
      return d.toLocaleString("fr-FR", {
        year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit",
      });
    } catch { return "N/A"; }
  };

  const rawStatus = ticket.status || "";
  const statusLabel = TICKET_STATUS_LABELS[rawStatus] || rawStatus || "Non assigné";
  const statusBadge = getStatusVariant(rawStatus, ticket.billing_method?.id);
  const intervenants = [...groupNames, ...userNames];
  const isEnCours = rawStatus === "in_progress";

  const handleCloseTicket = () => {
    if (!selectedFacturationMode) {
      toast.error("Veuillez sélectionner une méthode de facturation");
      return;
    }
    closeMutation.mutate({ id: ticket.id, facturationModeId: selectedFacturationMode }, {
      onSuccess: () => {
        toast.success("Ticket clôturé !");
        setShowConfirmClose(false);
        setSelectedFacturationMode(null);
      },
      onError: (err) => {
        toast.error("Erreur: " + err.message);
        setShowConfirmClose(false);
      },
    });
  };

  const btnStyle = {
    width: "32px", height: "32px", padding: 0,
    display: "inline-flex", alignItems: "center", justifyContent: "center",
  };

  return (
    <>
      {asCard ? (
        // Mobile card view
        <Card className="h-100 shadow-sm">
          <Card.Body className="d-flex flex-column gap-2">
            {/* Header row: ID + status badge */}
            <div className="d-flex justify-content-between align-items-center">
              <span className="text-muted small">#{ticket.id}</span>
              <Badge bg={statusBadge} text={statusNeedsDarkText(statusBadge) ? "dark" : undefined}>{statusLabel}</Badge>
            </div>

            <div className="fw-bold" style={{ fontSize: "0.95rem" }}>
              {ticket.title}
            </div>

            <div className="small text-muted">
              <span className="fw-semibold">Début :</span>{" "}
              {formatDate(ticket.start_date || ticket.created_at)}
            </div>

            {/* Action buttons */}
            <div className="d-flex gap-2 mt-auto pt-2 border-top flex-wrap">
              <Button variant="secondary" size="sm" title="Voir le ticket" onClick={() => onNavigate(ticket.id)} style={{ width: "32px", height: "32px", padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                <FaEye />
              </Button>
              {hasPermission('CREATE_INTERVENTION') && (
                <Button variant="primary" size="sm" title="Ajouter une intervention" onClick={() => setShowNewIntervention(true)} style={{ width: "32px", height: "32px", padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                  <FaPlus style={{ color: "white" }} />
                </Button>
              )}
              {isEnCours && hasPermission('CLOSE_TICKET') && (
                <Button variant="warning" size="sm" title="Clôturer le ticket" onClick={() => setShowConfirmClose(true)} disabled={closeMutation.isLoading} style={{ width: "32px", height: "32px", padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                  <FaDollarSign style={{ color: "white" }} />
                </Button>
              )}
            </div>
          </Card.Body>
        </Card>
      ) : (
        // Desktop table row
        <tr>
          <td className="align-middle">{ticket.id}</td>
          <td className="align-middle">{ticket.title}</td>
          <td className="align-middle">{formatDate(ticket.start_date || ticket.created_at)}</td>
          <td className="align-middle">
            {intervenants.length > 0 ? intervenants.join(", ") : "Non assigné"}
          </td>
          <td className="align-middle">
            <div style={{ maxHeight: "80px", overflowY: "auto", fontSize: "0.875rem" }}>
              {sortedInterventions.length > 0 ? (
                sortedInterventions.map((i, idx) => (
                  <div key={i.id || idx} style={{ padding: "2px 0", borderBottom: idx < sortedInterventions.length - 1 ? "1px solid #ddd" : "none", fontSize: "0.875rem" }}>
                    {formatDate(i.start_date || i.created_at)}
                  </div>
                ))
              ) : (
                <span className="text-muted">Aucune intervention</span>
              )}
            </div>
          </td>
          <td className="align-middle text-center">
            <Badge bg={statusBadge} text={statusNeedsDarkText(statusBadge) ? "dark" : undefined}>{statusLabel}</Badge>
          </td>
          <td className="align-middle text-center">
            <div className="d-flex gap-2 justify-content-center">
              <Button variant="secondary" size="sm" title="Voir le ticket" onClick={() => onNavigate(ticket.id)} style={btnStyle}>
                <FaEye />
              </Button>
              {hasPermission('CREATE_INTERVENTION') && (
                <Button variant="primary" size="sm" title="Ajouter une intervention" onClick={() => setShowNewIntervention(true)} style={btnStyle}>
                  <FaPlus style={{ color: "white" }} />
                </Button>
              )}
              {isEnCours && hasPermission('CLOSE_TICKET') && (
                <Button variant="warning" size="sm" title="Clôturer le ticket" onClick={() => setShowConfirmClose(true)} disabled={closeMutation.isLoading} style={btnStyle}>
                  <FaDollarSign style={{ color: "white" }} />
                </Button>
              )}
            </div>
          </td>
        </tr>
      )}

      <Modal show={showConfirmClose} onHide={() => setShowConfirmClose(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Clôturer le ticket</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Êtes-vous sûr de vouloir clôturer ce ticket ?</p>
          <Form.Group className="mt-3">
            <Form.Label>Méthode de facturation <span className="text-danger">*</span></Form.Label>
            {facturationLoading ? (
              <div className="text-center py-2"><Spinner animation="border" size="sm" /></div>
            ) : (
              <Form.Select value={selectedFacturationMode || ""} onChange={(e) => setSelectedFacturationMode(Number(e.target.value))} required>
                <option value="">Sélectionner une méthode</option>
                {[
                  ...(hasContract ? [CONTRAT_MAINTENANCE] : []),
                  ...facturationModes.filter(m => m.id !== 1 && (m.is_active || m.is_default)),
                ].map(mode => <option key={mode.id} value={mode.id}>{mode.label}</option>)}
              </Form.Select>
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmClose(false)}>Annuler</Button>
          <Button variant="warning" onClick={handleCloseTicket} disabled={closeMutation.isPending || !selectedFacturationMode}>
            {closeMutation.isPending ? "Clôture..." : "Clôturer"}
          </Button>
        </Modal.Footer>
      </Modal>

      <NewIntervention showNewIntervention={showNewIntervention} setShowNewIntervention={setShowNewIntervention} ticketId={ticket.id} />
    </>
  );
};

const TicketsTab = ({ client }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = useAuth();

  const [showNewTicket, setShowNewTicket] = useState(false);
  const [showFilters,   setShowFilters]   = useState(false);
  const [isMobile,      setIsMobile]      = useState(window.innerWidth < MOBILE_BREAKPOINT);
  const [sortField,     setSortField]     = useState("date_debut");
  const [sortOrder,     setSortOrder]     = useState("DESC");

  // Handle window resize for responsive behavior
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ── Search & pagination ───────────────────────────────────────────
  const [search,          setSearch]          = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page,            setPage]            = useState(1);
  const [limit,           setLimit]           = useState(10);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(t);
  }, [search]);

  // ── Filter state ──────────────────────────────────────────────────────────
  const [filterStatuses,    setFilterStatuses]    = useState(new Set(ALL_STATUSES));
  const [filterDescription, setFilterDescription] = useState("");
  const [debouncedDesc,     setDebouncedDesc]     = useState("");
  const [filterType,        setFilterType]        = useState(null);
  const [filterPriorite,    setFilterPriorite]    = useState(null);
  const [filterFacturation, setFilterFacturation] = useState(null);
  const [filterApprouveur,  setFilterApprouveur]  = useState(null);
  const [filterDateDebut,   setFilterDateDebut]   = useState({ op: ">=", val: "" });
  const [filterDateFin,     setFilterDateFin]     = useState({ op: "<=", val: "" });
  const [filterDateCloture, setFilterDateCloture] = useState({ op: "=",  val: "" });

  useEffect(() => {
    const t = setTimeout(() => setDebouncedDesc(filterDescription), 500);
    return () => clearTimeout(t);
  }, [filterDescription]);

  // ── Options for selects ───────────────────────────────────────────────────
  const { data: typesData }     = useTicketTypes();
  const typeOptions             = (typesData?.data ?? []).map(t => ({ value: t.id, label: t.label }));
  const { data: prioritesData } = useTicketPriorities();
  const prioriteOptions         = (prioritesData?.data ?? []).map(p => ({ value: p.id, label: p.label }));
  const { data: factModesData } = useFacturationModes();
  const factOptions             = (factModesData?.data ?? []).map(f => ({ value: f.id, label: f.label }));
  const { data: usersData }     = useUsers();
  const userOptions             = (usersData?.data ?? []).map(u => ({ value: u.id, label: `${u.name} ${u.surname}`.trim() }));

  // ── API filters ───────────────────────────────────────────────────────────
  const apiFilters = useMemo(() => ({
    clientId:    client?.id,
    statuses:    [...filterStatuses],
    description: debouncedDesc,
    type:        filterType?.value        ?? null,
    priorite:    filterPriorite?.value    ?? null,
    facturation: filterFacturation?.value ?? null,
    approuveur:  filterApprouveur?.value  ?? null,
    dateDebut:   filterDateDebut.val  ? filterDateDebut  : null,
    dateFin:     filterDateFin.val    ? filterDateFin    : null,
    dateCloture: filterDateCloture.val ? filterDateCloture : null,
  }), [client?.id, filterStatuses, debouncedDesc, filterType, filterPriorite,
      filterFacturation, filterApprouveur, filterDateDebut, filterDateFin, filterDateCloture]);

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (filterStatuses.size < ALL_STATUSES.length) n++;
    if (debouncedDesc)         n++;
    if (filterType)            n++;
    if (filterPriorite)        n++;
    if (filterFacturation)     n++;
    if (filterApprouveur)      n++;
    if (filterDateDebut.val)   n++;
    if (filterDateFin.val)     n++;
    if (filterDateCloture.val) n++;
    return n;
  }, [filterStatuses, debouncedDesc, filterType, filterPriorite,
      filterFacturation, filterApprouveur, filterDateDebut, filterDateFin, filterDateCloture]);

  // ── Handlers ───────────────────────────────────────────────────────────
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(prev => prev === "ASC" ? "DESC" : "ASC");
    } else {
      setSortField(field);
      setSortOrder("DESC");
    }
    setPage(1);
  };

  const toggleStatus = (s) => {
    setFilterStatuses(prev => {
      const next = new Set(prev);
      next.has(s) ? next.delete(s) : next.add(s);
      return next;
    });
    setPage(1);
  };

  const resetFilters = () => {
    setFilterStatuses(new Set(ALL_STATUSES));
    setFilterDescription("");
    setDebouncedDesc("");
    setFilterType(null);
    setFilterPriorite(null);
    setFilterFacturation(null);
    setFilterApprouveur(null);
    setFilterDateDebut({ op: ">=", val: "" });
    setFilterDateFin({ op: "<=", val: "" });
    setFilterDateCloture({ op: "=",  val: "" });
    setPage(1);
  };

  const handleNavigate = (ticketId) => {
    navigate(`/ticket/${ticketId}`, { state: { from: location.pathname, listState: {} } });
  };

  // ── Data ─────────────────────────────────────────────────────────────
  const { data: ticketsData, isLoading: loading, isError } = useTickets(
    page, limit, debouncedSearch, apiFilters, sortField, sortOrder
  );
  const tickets    = Array.isArray(ticketsData?.data?.tickets) ? ticketsData.data.tickets : [];
  const totalItems = ticketsData?.data?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalItems / limit));

  // ── Sortable header helper ─────────────────────────────────────────────
  const SortTh = ({ label, field }) => (
    <th onClick={() => handleSort(field)} style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}>
      {label}{" "}
      {sortField === field
        ? (sortOrder === "ASC" ? "▲" : "▼")
        : <span style={{ opacity: 0.25 }}>▲</span>}
    </th>
  );

  if (loading) {
    return <div className="text-center py-5"><Spinner animation="border" style={{ color: "#f6c23e" }} /></div>;
  }

  return (
    <>
      {showNewTicket && <NewTicket showNewTicket={showNewTicket} setShowNewTicket={setShowNewTicket} defaultClient={client} />}

      {/* Toolbar */}
      <div className="d-flex align-items-center justify-content-between mb-3 mt-3 gap-2 flex-wrap">
        <div className="d-flex align-items-center gap-2 flex-wrap" style={{ flex: "1 1 auto" }}>
          <div style={{ maxWidth: "70px", minWidth: "60px" }}>
            <Form.Select value={limit} onChange={e => { setLimit(Number(e.target.value)); setPage(1); }} size="sm">
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </Form.Select>
          </div>
          <div style={{ flex: "1 1 200px", maxWidth: "300px" }}>
            <InputGroup size="sm">
              <Form.Control
                placeholder="Recherche par titre..."
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
              />
            </InputGroup>
          </div>
          <Button
            variant={showFilters || activeFilterCount > 0 ? "warning" : "outline-secondary"}
            size="sm"
            className="d-flex align-items-center gap-2 filter-button"
            onClick={() => setShowFilters(prev => !prev)}
          >
            <FaFilter />
            <span>Filtres</span>
            {activeFilterCount > 0 && <Badge bg="light" text="dark" pill className="ms-1">{activeFilterCount}</Badge>}
          </Button>
        </div>
        {hasPermission('CREATE_TICKET') && (
          <button className="btn btn-warning btn-icon-split" onClick={() => setShowNewTicket(true)}>
            <span className="icon text-white-50" style={{ alignItems: "center", justifyContent: "center" }}>
              <BsBookmarkPlusFill style={{ color: "#fff" }} />
            </span>
            <span className="text">Ajouter un ticket</span>
          </button>
        )}
      </div>

      {/* Filter panel */}
      <Collapse in={showFilters}>
        <div>
          <div className="border rounded p-3 mb-3 bg-body-secondary">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <span className="fw-semibold text-muted small text-uppercase">Filtres avancés</span>
              {activeFilterCount > 0 && (
                <button className="btn btn-danger btn-icon-split btn-sm" onClick={resetFilters}>
                  <span className="icon text-white-50" style={{ alignItems: "center", justifyContent: "center" }}>
                    <FaTimes style={{ color: "#fff" }} />
                  </span>
                  <span className="text">Réinitialiser</span>
                </button>
              )}
            </div>

            {/* Statut */}
            <div className="mb-3">
              <Form.Label className="small fw-semibold mb-1">Statut</Form.Label>
              <div className="d-flex gap-3 flex-wrap">
                {ALL_STATUSES.map(s => (
                  <Form.Check
                    key={s}
                    type="checkbox"
                    id={`tt-${client?.id}-status-${s}`}
                    checked={filterStatuses.has(s)}
                    onChange={() => toggleStatus(s)}
                    label={
                      <Badge bg={FILTER_BADGE[s]} text={statusNeedsDarkText(FILTER_BADGE[s]) ? "dark" : undefined}>
                        {TICKET_STATUS_LABELS[s]}
                      </Badge>
                    }
                  />
                ))}
              </div>
            </div>

            {/* Description */}
            <div className="mb-3">
              <Form.Label className="small fw-semibold mb-1">Description</Form.Label>
              <Form.Control
                size="sm"
                type="text"
                placeholder="Filtrer par description..."
                value={filterDescription}
                onChange={e => { setFilterDescription(e.target.value); setPage(1); }}
              />
            </div>

            {/* Type + Priorité + Facturation */}
            <Row className="mb-3 g-3">
              <Col md={4}>
                <Form.Label className="small fw-semibold mb-1">Type</Form.Label>
                <Select isClearable value={filterType} onChange={opt => { setFilterType(opt); setPage(1); }} options={typeOptions} placeholder="Tous les types" styles={customSelectStyles} />
              </Col>
              <Col md={4}>
                <Form.Label className="small fw-semibold mb-1">Priorité</Form.Label>
                <Select isClearable value={filterPriorite} onChange={opt => { setFilterPriorite(opt); setPage(1); }} options={prioriteOptions} placeholder="Toutes" styles={customSelectStyles} />
              </Col>
              <Col md={4}>
                <Form.Label className="small fw-semibold mb-1">Facturation</Form.Label>
                <Select isClearable value={filterFacturation} onChange={opt => { setFilterFacturation(opt); setPage(1); }} options={factOptions} placeholder="Toutes" styles={customSelectStyles} />
              </Col>
            </Row>

            {/* Approuveur */}
            <Row className="mb-3 g-3">
              <Col md={6}>
                <Form.Label className="small fw-semibold mb-1">Approuveur</Form.Label>
                <Select isClearable value={filterApprouveur} onChange={opt => { setFilterApprouveur(opt); setPage(1); }} options={userOptions} placeholder="Tous" styles={customSelectStyles} />
              </Col>
            </Row>

            {/* Dates */}
            <Row className="g-3">
              {[
                { label: "Date de début",   state: filterDateDebut,   setState: setFilterDateDebut,   defaultOp: ">=" },
                { label: "Date de fin",     state: filterDateFin,     setState: setFilterDateFin,     defaultOp: "<=" },
                { label: "Date de clôture", state: filterDateCloture, setState: setFilterDateCloture, defaultOp: "="  },
              ].map(({ label, state, setState, defaultOp }) => (
                <Col md={4} key={label}>
                  <Form.Label className="small fw-semibold mb-1">{label}</Form.Label>
                  <InputGroup size="sm">
                    <Form.Select value={state.op} onChange={e => setState(prev => ({ ...prev, op: e.target.value }))} style={{ maxWidth: "60px" }}>
                      {OP_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </Form.Select>
                    <Form.Control type="date" value={state.val} onChange={e => { setState(prev => ({ ...prev, val: e.target.value })); setPage(1); }} />
                    {state.val && (
                      <Button variant="outline-secondary" size="sm" onClick={() => setState({ op: defaultOp, val: "" })}>
                        <FaTimes size={10} />
                      </Button>
                    )}
                  </InputGroup>
                </Col>
              ))}
            </Row>
          </div>
        </div>
      </Collapse>

      <div className="row">
        <div className="col-12">
          <Card className="border-left-warning shadow mb-4">
            <Card.Body>
              {isError ? (
                <p className="text-danger">Erreur lors du chargement des tickets.</p>
              ) : tickets.length === 0 && !loading ? (
                <p className="text-muted text-center py-4">Aucun ticket à afficher.</p>
              ) : (
                <>
                  {isMobile ? (
                    // Mobile view: Cards
                    <div className="row g-3">
                      {tickets.map(ticket => (
                        <div key={ticket.id} className="col-12">
                          <TicketMiniRow ticket={ticket} onNavigate={handleNavigate} clientId={client?.id} asCard />
                        </div>
                      ))}
                    </div>
                  ) : (
                    // Desktop view: Table
                    <Table striped hover responsive className="align-middle mb-0">
                      <thead>
                        <tr>
                          <SortTh label="ID"         field="id" />
                          <SortTh label="Titre"      field="titre" />
                          <SortTh label="Date début" field="date_debut" />
                          <th>Intervenant(s)</th>
                          <th>Intervention(s)</th>
                          <SortTh label="Statut"     field="status" />
                          <th>Action(s) réalisable(s)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tickets.map(ticket => (
                          <TicketMiniRow key={ticket.id} ticket={ticket} onNavigate={handleNavigate} clientId={client?.id} />
                        ))}
                      </tbody>
                    </Table>
                  )}
                  {totalPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <div className="text-muted fst-italic">Total : {totalItems} tickets</div>
                      <Pagination className="mb-0">
                        <Pagination.Prev onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Précédent</Pagination.Prev>
                        {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => i + 1).map(p => (
                          <Pagination.Item key={p} active={p === page} onClick={() => setPage(p)}>{p}</Pagination.Item>
                        ))}
                        <Pagination.Next onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Suivant</Pagination.Next>
                      </Pagination>
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </div>
      </div>
    </>
  );
};

export default TicketsTab;
